import React, { useState } from "react";
import { useRoles, useCreateRole } from "../hooks/useRoles";
import { Spinner } from "../components/ui/Spinner";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import type { Role } from "../domain/types/admin.types";

const ROLE_PREFIX = "ROLE_";
const ROLE_NAME_RE = /^ROLE_[A-Z0-9_]+$/;

export const RolesPage: React.FC = () => {
  const { data: roles, isLoading, isError, error } = useRoles();
  const createRole = useCreateRole();

  const [showForm, setShowForm] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [description, setDescription] = useState("");
  const [validationError, setValidationError] = useState("");

  const normalise = (raw: string): string => {
    const trimmed = raw.trim().toUpperCase();
    return trimmed.startsWith(ROLE_PREFIX) ? trimmed : `${ROLE_PREFIX}${trimmed}`;
  };

  const handleCreate = async () => {
    const name = normalise(inputValue);
    if (!ROLE_NAME_RE.test(name)) {
      setValidationError("Only uppercase letters, digits, and underscores after ROLE_");
      return;
    }
    const exists = roles?.some((r: Role) => r.name === name);
    if (exists) {
      setValidationError(`${name} already exists`);
      return;
    }
    try {
      await createRole.mutateAsync({ name, description: description.trim() || undefined });
      setInputValue("");
      setDescription("");
      setShowForm(false);
    } catch (err: any) {
      setValidationError(err?.data?.message ?? "Failed to create role.");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleCreate();
    if (e.key === "Escape") {
      setShowForm(false);
      setInputValue("");
      setDescription("");
      setValidationError("");
    }
  };

  const preview = inputValue.trim() ? normalise(inputValue) : "";

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Roles</h2>
          {roles && (
            <p className="text-xs text-gray-400 mt-0.5">{roles.length} roles</p>
          )}
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)}>+ Create Role</Button>
        )}
      </div>

      {/* Create form */}
      {showForm && (
        <div className="bg-white rounded border border-border-gray px-5 py-4 flex flex-col gap-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">New Role</p>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-mono text-gray-400 select-none whitespace-nowrap">
                ROLE_
              </span>
              <Input
                autoFocus
                value={inputValue.startsWith(ROLE_PREFIX) ? inputValue.slice(ROLE_PREFIX.length) : inputValue}
                onChange={(e) => {
                  const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "");
                  setInputValue(raw);
                  setValidationError("");
                }}
                onKeyDown={handleKeyDown}
                placeholder="MY_FEATURE_ADMIN"
                error={!!validationError}
                className="w-full font-mono"
              />
            </div>
            {preview && (
              <p className="text-xs text-gray-400 ml-10">
                Will be created as: <span className="font-mono font-medium text-gray-700">{preview}</span>
              </p>
            )}
            {validationError && (
              <p className="text-xs text-red-500 ml-10">{validationError}</p>
            )}
          </div>
          <div className="flex flex-col gap-1 ml-10">
            <label className="text-xs font-medium text-gray-700">
              Description <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description of what this role grants"
              className="w-full"
              maxLength={120}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleCreate}
              disabled={createRole.isPending || !inputValue.trim()}
            >
              {createRole.isPending ? (
                <span className="flex items-center gap-2">
                  <Spinner className="h-4 w-4 border-white border-r-transparent" />
                  Creating…
                </span>
              ) : (
                "Create"
              )}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowForm(false);
                setInputValue("");
                setDescription("");
                setValidationError("");
                createRole.reset();
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Roles list */}
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400 py-8">
          <Spinner /> Loading roles…
        </div>
      ) : isError ? (
        <div className="py-8 text-sm text-red-500">
          {(error as any)?.data?.message ?? "Failed to load roles."}
        </div>
      ) : !roles || roles.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-400">No roles found.</div>
      ) : (
        <div className="overflow-x-auto rounded border border-border-gray bg-white">
          <table className="w-full min-w-[1024px] text-sm table-fixed">
            <colgroup>
              <col className="w-1/2" />
              <col className="w-1/2" />
            </colgroup>
            <thead>
              <tr className="border-b border-border-gray bg-default-99">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Role Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Description
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-gray">
              {(roles as Role[]).map((role: Role) => (
                <tr key={role.id} className="hover:bg-default-99 transition-colors">
                  <td className="px-4 py-3 font-mono font-medium text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis">
                    {role.name}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {role.description ?? <span className="text-gray-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
